import { useCallback, useState } from 'react';

import { BillingScreen } from '../features/billing/screens/BillingScreen';
import { TransactionHistoryScreen } from '../features/billing/screens/TransactionHistoryScreen';
import { FaqScreen } from '../features/faq/screens/FaqScreen';
import { EveningReminderSettingsScreen } from '../features/settings/screens/EveningReminderSettingsScreen';
import { ProcrastinationScreen } from '../features/procrastination/screens/ProcrastinationScreen';
import { ReportsScreen } from '../features/reports/screens/ReportsScreen';
import { BacklogScreen } from '../features/tasks/screens/BacklogScreen';
import { TaskDetailScreen } from '../features/tasks/screens/TaskDetailScreen';
import { TaskEditScreen } from '../features/tasks/screens/TaskEditScreen';
import { TaskFormScreen } from '../features/tasks/screens/TaskFormScreen';
import { Header } from '../shared/ui/Header';
import { TabBar, type Tab } from '../shared/ui/TabBar';
import { useScreenStack } from './navigation/useScreenStack';
import { useTelegramBackButton } from './navigation/useTelegramBackButton';
import styles from './AppShell.module.css';

// PARA-105 §6/§7 — layout: Header + TabBar + рендер текущего экрана из стека.
export function AppShell(): JSX.Element {
  const [tab, setTab] = useState<Tab>('backlog');
  const { current, push, pop } = useScreenStack();

  useTelegramBackButton(current !== null, pop);

  // PARA-131 (AD-7, AC-1) — проверку «биллинг уже открыт» делает AppShell:
  // второй экземпляр в стеке заставил бы нажимать «Назад» дважды на одном и
  // том же экране. Header про стек экранов не знает.
  const handleOpenBilling = useCallback(() => {
    if (current?.type === 'billing') {
      return;
    }
    // PARA-140: история платежей пушится поверх биллинга — вход в биллинг из
    // шапки с экрана истории это «Назад», а не второй billing в стеке.
    if (current?.type === 'billing-history') {
      pop();
      return;
    }
    push({ type: 'billing' });
  }, [current, push, pop]);

  // PARA-140 (AC-4) — история платежей: та же защита от повторного пуша, что
  // и у биллинга. Экран пушится поверх биллинга, поэтому «Назад» возвращает
  // на BillingScreen.
  const handleOpenTransactionHistory = useCallback(() => {
    if (current?.type === 'billing-history') {
      return;
    }
    push({ type: 'billing-history' });
  }, [current, push]);

  // PARA-133 (AC-1a, EC-1) — та же защита от повторного пуша, что и у биллинга:
  // второй экземпляр справки в стеке заставил бы нажимать «Назад» дважды.
  const handleOpenFaq = useCallback(() => {
    if (current?.type === 'faq') {
      return;
    }
    push({ type: 'faq' });
  }, [current, push]);

  // PARA-136 — та же защита от повторного пуша.
  const handleOpenSettings = useCallback(() => {
    if (current?.type === 'evening-reminder-settings') {
      return;
    }
    push({ type: 'evening-reminder-settings' });
  }, [current, push]);

  let content: JSX.Element;
  if (current?.type === 'task-detail') {
    content = (
      <TaskDetailScreen
        taskId={current.taskId}
        onEditTask={(taskId) => push({ type: 'edit-task', taskId })}
        onOpenBilling={handleOpenBilling}
      />
    );
  } else if (current?.type === 'create-task') {
    content = <TaskFormScreen onClose={pop} onOpenBilling={handleOpenBilling} />;
  } else if (current?.type === 'edit-task') {
    content = (
      <TaskEditScreen taskId={current.taskId} onClose={pop} onOpenBilling={handleOpenBilling} />
    );
  } else if (current?.type === 'procrastination') {
    content = <ProcrastinationScreen onClose={pop} />;
  } else if (current?.type === 'billing') {
    content = <BillingScreen onOpenTransactionHistory={handleOpenTransactionHistory} />;
  } else if (current?.type === 'billing-history') {
    content = <TransactionHistoryScreen />;
  } else if (current?.type === 'faq') {
    content = <FaqScreen articleId={current.articleId} />;
  } else if (current?.type === 'evening-reminder-settings') {
    content = <EveningReminderSettingsScreen />;
  } else if (tab === 'reports') {
    content = <ReportsScreen />;
  } else {
    content = (
      <BacklogScreen
        onOpenTask={(taskId) => push({ type: 'task-detail', taskId })}
        onProcrastinate={() => push({ type: 'procrastination' })}
        onCreateTask={() => push({ type: 'create-task' })}
        onEditTask={(taskId) => push({ type: 'edit-task', taskId })}
      />
    );
  }

  return (
    <div className={styles.shell}>
      <Header
        onOpenBilling={handleOpenBilling}
        onOpenFaq={handleOpenFaq}
        onOpenSettings={handleOpenSettings}
      />
      <main className={styles.content}>{content}</main>
      {current === null && <TabBar active={tab} onChange={setTab} />}
    </div>
  );
}
